import { useState, useCallback, useRef, useEffect } from 'react';
import {
  TabletopState,
  CardState,
  DeckState,
  DeckOperation,
  NotificationState,
  Position,
  SelectedItem,
  HistoryEntry,
  AnimationState,
  DeckInsertionPosition
} from '../types';
import { v4 as uuidv4 } from 'uuid';
import { NOTIFICATION_DURATION, MAX_HISTORY_ENTRIES } from '../constants';
import { useAnimations } from './useAnimations';
import { applyTabletopAction, TabletopAction, TabletopEvent } from '@/state/tabletopActions';

interface UseTabletopStateProps {
  initialState?: TabletopState;
  onAction?: (action: TabletopAction) => void;
}

export const useTabletopState = ({ initialState, onAction }: UseTabletopStateProps = {}) => {
  // Animations hook
  const {
    createDealAnimation,
    createFlipAnimation,
    createRotateAnimation
  } = useAnimations();

  const legacyInitialState = initialState as (TabletopState & { damageCounters?: TabletopState['tokens'] }) | undefined;

  // State for the tabletop
  const [tabletopState, setTabletopState] = useState<TabletopState>(() => ({
    cards: legacyInitialState?.cards ?? {},
    decks: legacyInitialState?.decks ?? {},
    dice: legacyInitialState?.dice ?? {},
    tokens: legacyInitialState?.tokens ?? legacyInitialState?.damageCounters ?? {},
    coins: legacyInitialState?.coins ?? {},
    hand: legacyInitialState?.hand ?? {},
    handOrder: legacyInitialState?.handOrder ?? [],
    history: legacyInitialState?.history ?? [],
    cardPiles: legacyInitialState?.cardPiles ?? {},
  }));

  const latestStateRef = useRef<TabletopState>(tabletopState);
  useEffect(() => {
    latestStateRef.current = tabletopState;
  }, [tabletopState]);
  
  const playerIdRef = useRef<string | null>(null);
  const setPlayerId = useCallback((playerId: string | null) => {
    playerIdRef.current = playerId;
  }, []);

  // Selected card or deck
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  
  // Card being zoomed in on
  const [zoomedCard, setZoomedCard] = useState<string | null>(null);
  
  // Notification state
  const [notification, setNotification] = useState<NotificationState | null>(null);
  const notificationTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Append to action history (newest first)
  const logAction = useCallback((message: string) => {
    const entry: HistoryEntry = {
      id: uuidv4(),
      message,
      timestamp: Date.now()
    };

    setTabletopState(prev => ({
      ...prev,
      history: [entry, ...(prev.history || [])].slice(0, MAX_HISTORY_ENTRIES)
    }));
  }, []);
  
  // Show a notification
  const showNotification = useCallback((message: string, type: 'success' | 'error' | 'info' = 'info') => {
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
      notificationTimeoutRef.current = null;
    }
    setNotification({
      message,
      type,
      timestamp: Date.now()
    });
    
    // Auto-hide notification after a delay
    notificationTimeoutRef.current = setTimeout(() => {
      setNotification(null);
      notificationTimeoutRef.current = null;
    }, NOTIFICATION_DURATION);
  }, []);

  useEffect(() => {
    return () => {
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
        notificationTimeoutRef.current = null;
      }
    };
  }, []);

  const processEvents = useCallback((events: TabletopEvent[]) => {
    events.forEach(event => {
      if (event.type === 'notification') {
        showNotification(event.message, event.level);
      } else if (event.type === 'history') {
        logAction(event.message);
      }
    });
  }, [showNotification, logAction]);

  const dispatchAction = useCallback(
    (action: TabletopAction, options?: { silent?: boolean }) => {
      let result: ReturnType<typeof applyTabletopAction> | null = null;

      try {
        const applied = applyTabletopAction(latestStateRef.current, action, {
          createDealAnimation,
          createFlipAnimation,
          createRotateAnimation,
          generateId: uuidv4,
          randomInt: (sides: number) => Math.floor(Math.random() * sides) + 1,
          currentPlayerId: playerIdRef.current ?? undefined
        });
        latestStateRef.current = applied.state;
        setTabletopState(applied.state);
        result = applied;
      } catch (error) {
        console.error('applyTabletopAction failed', action, error);
        throw error;
      }

      if (!result) {
        throw new Error('Failed to apply tabletop action.');
      }

      // Safeguard: Warn if actions that should produce events don't
      const actionsShouldProduceEvents = [
        'recordHistoryEntry',
        'moveDeckCardsToTable',
        'moveDeckCardsToHand',
        'moveDeckCardsToDeck',
        'importDeck'
      ];
      if (actionsShouldProduceEvents.includes(action.type) && result.events.length === 0) {
        console.error(
          `⚠️ BUG DETECTED: Action "${action.type}" should produce events but returned 0!`,
          'This likely means the action handler is in the wrong function or missing.',
          'Action:', action
        );
      }

      if (result.events.length > 0) {
        processEvents(result.events);
      }

      if (!options?.silent && onAction) {
        onAction(action);
      }

      return result;
    },
    [setTabletopState, createDealAnimation, createFlipAnimation, createRotateAnimation, processEvents, onAction]
  );

  // Handle deck operations
  const handleDeckOperation = useCallback(
    (id: string, operation: DeckOperation) => {
      dispatchAction({ type: 'deckOperation', deckId: id, operation });
    },
    [dispatchAction]
  );

  // Handle card flip
  const handleCardFlip = useCallback(
    (id: string) => {
      dispatchAction({ type: 'flipCard', cardId: id });
    },
    [dispatchAction]
  );

  // Handle card rotation
  const handleCardRotate = useCallback(
    (id: string, targetRotation: number) => {
      dispatchAction({ type: 'rotateCard', cardId: id, rotation: targetRotation });
    },
    [dispatchAction]
  );

  const handleHandCardFlip = useCallback(
    (id: string) => {
      dispatchAction({ type: 'flipHandCard', cardId: id });
    },
    [dispatchAction]
  );

  const setHandCardSelection = useCallback(
    (id: string, selected: boolean) => {
      dispatchAction({ type: 'setHandCardSelected', cardId: id, selected });
    },
    [dispatchAction]
  );

  const placeHandCardOnTable = useCallback(
    (id: string, position: Position, options?: { faceUp?: boolean; rotation?: number; suppressLog?: boolean }) => {
      const result = dispatchAction({
        type: 'placeHandCard',
        cardId: id,
        position,
        faceUp: options?.faceUp,
        rotation: options?.rotation,
        suppressLog: options?.suppressLog
      });
      const card = result.state.cards[id] as CardState | undefined;
      return card ?? null;
    },
    [dispatchAction]
  );

  // Move an existing tabletop card back into the hand
  const moveCardToHand = useCallback(
    (id: string) => {
      const result = dispatchAction({ type: 'moveCardToHand', cardId: id });
      if (result.state.hand[id]) {
        setSelectedItem(null);
      }
    },
    [dispatchAction, setSelectedItem]
  );

  // Play a card from the private hand onto the tabletop
  const playCardFromHand = useCallback(
    (id: string, position: Position, options?: { faceUp?: boolean; rotation?: number }) => {
      dispatchAction({
        type: 'placeHandCard',
        cardId: id,
        position,
        faceUp: options?.faceUp,
        rotation: options?.rotation
      });
    },
    [dispatchAction]
  );

  // Function to add a test die
  const addTestDice = useCallback(
    (centerPosition: Position) => {
      dispatchAction({ type: 'addTestDice', id: uuidv4(), position: centerPosition });
    },
    [dispatchAction]
  );

  // Function to add a tabletop token
  const addToken = useCallback(
    (centerPosition: Position, text?: string, color?: string) => {
      dispatchAction({ type: 'addToken', id: uuidv4(), position: centerPosition, text, color });
    },
    [dispatchAction]
  );

  const addCoin = useCallback(
    (centerPosition: Position) => {
      dispatchAction({ type: 'addCoin', id: uuidv4(), position: centerPosition });
    },
    [dispatchAction]
  );

  // Function to update an existing token
  const updateToken = useCallback(
    (id: string, text?: string, color?: string) => {
      dispatchAction({ type: 'updateToken', id, text, color });
    },
    [dispatchAction]
  );
  
  // Handle dice roll
  const handleDiceRoll = useCallback(
    (id: string) => {
      dispatchAction({ type: 'rollDice', diceId: id });
    },
    [dispatchAction]
  );

  const handleCoinFlip = useCallback(
    (id: string) => {
      dispatchAction({ type: 'flipCoin', coinId: id });
    },
    [dispatchAction]
  );
  
  // Import a deck
  const handleDeckImport = useCallback((deck: DeckState) => {
    const id = deck.id || uuidv4();

    if (deck.cards && deck.position && deck.name) {
      const nextDeck: DeckState = {
        ...deck,
        id
      };
      dispatchAction({ type: 'importDeck', deck: nextDeck });
      return;
    }

    const sourceCards = deck.cards ?? [];
    const cards: CardState[] = sourceCards.map(card => ({
      id: uuidv4(),
      position: { x: 0, y: 0 },
      rotation: 0,
      isFaceUp: false,
      imageUrl: card.imageUrl,
      backImageUrl:
        card.backImageUrl ??
        'https://cards.scryfall.io/normal/back/4/e/4ef4d7f0-dda2-4171-a7f1-8c40eb2f254c.jpg',
      name: card.name,
      counters: card.counters ?? {}
    }));

    const newDeck: DeckState = {
      id,
      position: deck.position || { x: 0, y: 0 },
      rotation: 0,
      name: deck.name || 'Imported Deck',
      cards,
      faceUp: false,
      collapsed: true
    };

    dispatchAction({ type: 'importDeck', deck: newDeck });
  }, [dispatchAction]);

  // Add card to deck
  const addCardToDeck = useCallback(
    (cardId: string, deckId: string, position: DeckInsertionPosition = 'top') => {
      const result = dispatchAction({ type: 'addCardToDeck', cardId, deckId, position });
      if (!result.state.cards[cardId]) {
        setSelectedItem(null);
      }
    },
    [dispatchAction, setSelectedItem]
  );

  const moveDeckCardsToTable = useCallback(
    (deckId: string, cardIds: string[], position: Position, options?: { faceUp?: boolean; rotation?: number; silent?: boolean }) => {
      if (cardIds.length === 0) {
        return;
      }
      dispatchAction(
        {
          type: 'moveDeckCardsToTable',
          deckId,
          cardIds,
          position,
          faceUp: options?.faceUp,
          rotation: options?.rotation
        },
        { silent: options?.silent }
      );
    },
    [dispatchAction]
  );

  const moveDeckCardsToHand = useCallback(
    (deckId: string, cardIds: string[], options?: { silent?: boolean }) => {
      if (cardIds.length === 0) {
        return;
      }
      dispatchAction(
        {
          type: 'moveDeckCardsToHand',
          deckId,
          cardIds
        },
        { silent: options?.silent }
      );
    },
    [dispatchAction]
  );

  const moveDeckCardsToDeck = useCallback(
    (
      sourceDeckId: string,
      targetDeckId: string,
      cardIds: string[],
      options?: { silent?: boolean; position?: DeckInsertionPosition }
    ) => {
      if (cardIds.length === 0) {
        return;
      }
      dispatchAction(
        {
          type: 'moveDeckCardsToDeck',
          sourceDeckId,
          targetDeckId,
          cardIds,
          position: options?.position
        },
        { silent: options?.silent }
      );
    },
    [dispatchAction]
  );

  const recordHistoryEntry = useCallback(
    (message: string, options?: { silent?: boolean }) => {
      dispatchAction(
        {
          type: 'recordHistoryEntry',
          message
        },
        { silent: options?.silent }
      );
    },
    [dispatchAction]
  );

  const moveTableItem = useCallback(
    (itemType: 'card' | 'deck' | 'dice' | 'token' | 'coin', id: string, position: Position, options?: { silent?: boolean }) => {
      dispatchAction(
        {
          type: 'moveTableItem',
          itemType,
          id,
          position
        },
        options
      );
    },
    [dispatchAction]
  );

  const moveCardGroup = useCallback(
    (cardIds: string[], positions: Record<string, Position>, options?: { silent?: boolean }) => {
      dispatchAction(
        {
          type: 'moveCardGroup',
          cardIds,
          positions
        },
        options
      );
    },
    [dispatchAction]
  );

  const rotateDeck = useCallback(
    (deckId: string, rotation: number, options?: { silent?: boolean }) => {
      dispatchAction(
        {
          type: 'rotateDeck',
          deckId,
          rotation
        },
        options
      );
    },
    [dispatchAction]
  );

  const removeTableItem = useCallback(
    (itemType: 'card' | 'deck' | 'dice' | 'token' | 'coin', id: string, options?: { silent?: boolean }) => {
      dispatchAction(
        {
          type: 'removeTableItem',
          itemType,
          id
        },
        options
      );
      setSelectedItem(prev => {
        if (prev?.id === id && prev.type === itemType) {
          return null;
        }
        return prev;
      });
    },
    [dispatchAction, setSelectedItem]
  );

  const applyRemoteState = useCallback(
    (state: TabletopState, options?: { events?: TabletopEvent[]; skipEvents?: boolean }) => {
      latestStateRef.current = state;
      setTabletopState(state);
      if (options?.events && !options.skipEvents) {
        options.events.forEach(event => {
          if (event.type === 'notification') {
            showNotification(event.message, event.level);
          }
        });
      }
    },
    [setTabletopState, showNotification]
  );

  // Handle zooming a card
  const handleCardZoom = useCallback((id: string) => {
    setZoomedCard(id);
  }, []);

  // Handle card animation complete
  const handleCardAnimationComplete = useCallback((id: string) => {
    // Remove the animation property from the card
    setTabletopState(prev => {
      const existingCard = prev.cards[id];
      if (!existingCard) return prev;

      const { animation, ...cardWithoutAnimation } = existingCard as CardState & {
        animation?: AnimationState;
      };
      void animation;

      return {
        ...prev,
        cards: {
          ...prev.cards,
          [id]: cardWithoutAnimation
        }
      };
    });
  }, []);

  return {
    tabletopState,
    setTabletopState,
    selectedItem,
    setSelectedItem,
    zoomedCard,
    setZoomedCard,
    notification,
    setNotification,
    handleDeckOperation,
    handleCardFlip,
    handleCardRotate, // New rotation handler
    handleHandCardFlip,
    setHandCardSelection,
    moveCardToHand,
    playCardFromHand,
    placeHandCardOnTable,
    handleDiceRoll,
    addTestDice,
    addToken,
    addCoin,
    updateToken,
    handleCoinFlip,
    handleDeckImport,
    addCardToDeck,
    moveDeckCardsToTable,
    moveDeckCardsToHand,
    moveDeckCardsToDeck,
    recordHistoryEntry,
    moveTableItem,
    moveCardGroup,
    rotateDeck,
    removeTableItem,
    showNotification,
    logAction,
    handleCardZoom,
    handleCardAnimationComplete,
    dispatchAction,
    applyRemoteState,
    setPlayerId
  };
};
